import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import PosterTemplatesSection from "@/components/PosterTemplatesSection";
import { Copy, ExternalLink, Sparkles, TrendingUp, Users, Wallet, Check, Quote, Share2, Handshake, BadgeCheck, MousePointerClick, ClipboardPaste, AlertCircle, Upload, Trash2, Image as ImageIcon } from "lucide-react";
import ReviewSubmitModal from "@/components/ReviewSubmitModal";
import AffiliateShareModal from "@/components/AffiliateShareModal";
import RecommendDialog from "@/components/RecommendDialog";
import BannerPreview from "@/components/AffiliateBannerPreview";
import { bicFromIban, bankFromIban, validateIban } from "@/lib/bicFromIban";

// localStorage key: set once the post-signup share prompt has been shown
// to a given ref_code. Prevents the modal from popping up on every page
// visit while still triggering the first time.
const SHARE_SEEN_KEY = "jobgrid_affiliate_share_seen_v1";

/**
 * Profile > Affiliate tab.
 *
 * Two states:
 *   - Not opted in → promotion card + "Word affiliate" button opens a small
 *     inline wizard (name + IBAN + BIC).
 *   - Opted in → stats grid (clicks, conversions, accrued €), banner HTML with
 *     copy-to-clipboard, and a payout-request button that shows once accrued
 *     ≥ €25.
 */
export default function AffiliatePanel() {
  const { t, lang } = useT();
  const [state, setState] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", iban: "", bic: "" });
  const [busy, setBusy] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [recDialogOpen, setRecDialogOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/affiliate/me");
      setState(r.data);
      // Pre-fill the join form with the suggested full name from the user's
      // profile so partners don't have to retype it. IBAN stays empty for
      // safety — bank rekening data must be entered consciously.
      if (r.data?.suggested_name && !r.data?.is_affiliate) {
        setForm((f) => f.full_name ? f : { ...f, full_name: r.data.suggested_name });
      }
      // Auto-open the share modal the first time an opted-in affiliate
      // sees this panel. Keyed by ref_code so a re-generated code (rare)
      // re-triggers the prompt.
      if (r.data?.is_affiliate && r.data?.ref_code) {
        const seen = (localStorage.getItem(SHARE_SEEN_KEY) || "").split(",");
        if (!seen.includes(r.data.ref_code)) {
          setShareOpen(true);
          localStorage.setItem(SHARE_SEEN_KEY, [...seen, r.data.ref_code].join(","));
        }
      }
    } catch { setState({ is_affiliate: false }); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const join = async () => {
    if (!form.full_name.trim() || !form.iban.trim()) {
      toast.error(t("affJoinValidationMissing"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/affiliate/join", form);
      toast.success(t("affJoinSuccess"));
      setWizardOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("affJoinFailed"));
    } finally { setBusy(false); }
  };

  const requestPayout = async () => {
    setBusy(true);
    try {
      const r = await api.post("/affiliate/payout-request");
      toast.success(t("affPayoutRequested", { n: r.data?.conversions ?? 0 }));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("affPayoutFailed"));
    } finally { setBusy(false); }
  };

  // Update IBAN and simultaneously auto-derive BIC if we recognise the
  // bank code. Users can still override BIC manually — we only fill it
  // when the field is currently empty (or was auto-filled from a prior
  // IBAN keystroke) so a manual entry is never silently overwritten.
  const updateIban = (rawValue) => {
    setForm((f) => {
      const derived = bicFromIban(rawValue);
      const shouldAutoFill = derived && (!f.bic || f._bic_autofilled);
      return {
        ...f,
        iban: rawValue,
        bic: shouldAutoFill ? derived : f.bic,
        _bic_autofilled: shouldAutoFill ? true : f._bic_autofilled,
      };
    });
  };

  const pasteIban = async () => {
    try {
      const txt = await navigator.clipboard.readText();
      if (!txt) return;
      const cleaned = txt.replace(/\s+/g, "").toUpperCase();
      updateIban(cleaned);
      toast.success(t("affIbanPasted"));
    } catch {
      toast.error(t("affIbanPasteFailed"));
    }
  };

  const copyBanner = async () => {
    // Fetch the full banner HTML from the recruiter/signature endpoint —
    // it's shared between the affiliate CTA and the recruiter email signature.
    try {
      const r = await api.get("/recruiter/signature");
      await navigator.clipboard.writeText(r.data.html);
      toast.success(t("affBannerCopied"));
    } catch {
      toast.error(t("affBannerCopyFailed"));
    }
  };

  const copyPlainLink = async () => {
    const url = state?.ref_code
      ? `${window.location.origin}/api/r/${state.ref_code}`
      : "";
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("affLinkCopied"));
    } catch {
      toast.error(t("affLinkCopyFailed"));
    }
  };

  if (!state) return <p className="font-mono text-xs text-neutral-500">…</p>;

  // ---- NOT-OPTED-IN promo state ----
  if (!state.is_affiliate) {
    return (
      <div data-testid="affiliate-panel-promo" className="space-y-6">
        {/* --- Hero: high-contrast dark card with headline commission stats.
             Note: we deliberately DO NOT use `ed-card` here — that global
             class forces `background:#FFFFFF` which would kill the dark
             gradient. Plain <div> with Tailwind + inline gradient wins. */}
        <div className="relative overflow-hidden bg-gradient-to-br from-[#1A1A1A] via-[#2A2724] to-[#3D3934] text-white shadow-xl">
          <div className="absolute -top-10 -right-10 opacity-[0.08] pointer-events-none">
            <Handshake size={220} strokeWidth={1.1} className="text-amber-300" />
          </div>
          <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
               style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #F5B301 0%, transparent 50%)" }} />

          <div className="relative z-10 p-8 sm:p-10">
            <div className="inline-flex items-center gap-2 border border-amber-300/40 bg-amber-300/10 px-3 py-1">
              <Sparkles size={11} strokeWidth={1.75} className="text-amber-300" />
              <span className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-amber-200">
                {t("affPromoEyebrow")}
              </span>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl mt-5 leading-tight text-white">
              {t("affPromoTitle")}
            </h2>
            <p className="text-sm sm:text-base leading-relaxed mt-4 max-w-xl text-neutral-100">
              {t("affPromoBody")}
            </p>

            {/* Stats strip — 4 headline facts */}
            <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-4 border-t border-white/10 pt-6">
              {[
                { v: "15%", l: t("affPromoStatCommission") },
                { v: "30d", l: t("affPromoStatCookie") },
                { v: "€25", l: t("affPromoStatPayout") },
                { v: "SEPA", l: t("affPromoStatMethod") },
              ].map((s, i) => (
                <div key={i} data-testid={`affiliate-promo-stat-${i}`}>
                  <p className="font-serif text-2xl sm:text-3xl text-amber-200">{s.v}</p>
                  <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-400 mt-1">
                    {s.l}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                data-testid="affiliate-open-wizard"
                onClick={() => {
                  setWizardOpen(true);
                  // Give the DOM one frame to render, then scroll the newly
                  // visible wizard into view so users don't wonder if the
                  // button did anything.
                  setTimeout(() => {
                    const el = document.querySelector('[data-testid="affiliate-wizard"]');
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                  }, 60);
                }}
                className="px-6 py-3.5 bg-amber-300 text-[#1A1A1A] font-mono text-xs uppercase tracking-[0.15em] hover:bg-amber-200 transition-colors inline-flex items-center gap-2 shadow-lg"
              >
                {t("affPromoCta")}
                <TrendingUp size={13} strokeWidth={2} />
              </button>
              <button
                onClick={() => setRecDialogOpen(true)}
                data-testid="affiliate-share-only"
                className="px-6 py-3.5 border border-white/50 text-white font-mono text-xs uppercase tracking-[0.15em] hover:bg-white/10 transition-colors inline-flex items-center gap-2"
                title={t("affPromoShareOnlyHint")}
              >
                <Share2 size={13} strokeWidth={2} />
                {t("affPromoShareOnly")}
              </button>
            </div>
            <p className="mt-3 font-mono text-[0.65rem] text-white/60 max-w-lg">
              {t("affPromoShareOnlyHint")}
            </p>
          </div>
        </div>

        {/* --- Process flow: how it works in 4 steps --- */}
        <div className="bg-white border border-black/10 p-6 sm:p-8">
          <p className="ed-eyebrow">{t("affFlowEyebrow")}</p>
          <h3 className="font-serif text-2xl mt-3 leading-tight">{t("affFlowTitle")}</h3>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4 relative">
            {/* Connecting line — desktop only */}
            <div className="hidden md:block absolute top-6 left-[12.5%] right-[12.5%] h-px bg-neutral-200 z-0" aria-hidden />
            {[
              { Icon: BadgeCheck, T: t("affFlowStep1T"), D: t("affFlowStep1D") },
              { Icon: Share2, T: t("affFlowStep2T"), D: t("affFlowStep2D") },
              { Icon: MousePointerClick, T: t("affFlowStep3T"), D: t("affFlowStep3D") },
              { Icon: Wallet, T: t("affFlowStep4T"), D: t("affFlowStep4D") },
            ].map(({ Icon, T, D }, i) => (
              <div key={i} data-testid={`affiliate-flow-step-${i}`} className="relative z-10 flex flex-col items-start">
                <div className="w-12 h-12 rounded-full bg-neutral-900 text-white flex items-center justify-center relative shadow-md">
                  <Icon size={18} strokeWidth={1.75} />
                  <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-300 text-neutral-900 font-mono text-[0.6rem] flex items-center justify-center font-bold">
                    {i + 1}
                  </span>
                </div>
                <p className="font-serif text-lg mt-4 leading-tight">{T}</p>
                <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{D}</p>
              </div>
            ))}
          </div>
        </div>

        {/* --- Banner preview: mock of the exact email-signature card users get --- */}
        <div className="bg-white border border-black/10 p-6 sm:p-8">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <p className="ed-eyebrow">{t("affBannerPreviewEyebrow")}</p>
              <h3 className="font-serif text-2xl mt-3 leading-tight">{t("affBannerPreviewTitle")}</h3>
              <p className="text-sm text-neutral-600 mt-2 max-w-lg leading-relaxed">
                {t("affBannerPreviewBody")}
              </p>
            </div>
            <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 border border-neutral-300 px-2 py-1">
              {t("affBannerPreviewLabel")}
            </span>
          </div>

          {/* Faithful visual preview of _build_signature_html (see recruiter_api.py). */}
          <div className="mt-6">
            <BannerPreview
            logoDataUrl={state?.logo_data_url || ""}
            displayName={state?.display_name || ""}
          />
          </div>

          <p className="mt-4 text-xs text-neutral-500 font-mono leading-relaxed">
            {t("affBannerPreviewHint")}
          </p>
        </div>

        {/* --- Trust strip: who this is for --- */}
        <div className="bg-gradient-to-br from-[#F9F8F6] to-[#F0EFEA] border border-black/10 p-6 sm:p-8">
          <p className="ed-eyebrow">{t("affForWhoEyebrow")}</p>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[t("affForWho1"), t("affForWho2"), t("affForWho3"), t("affForWho4")].map((line, i) => (
              <div key={i} className="flex items-center gap-2">
                <Users size={13} strokeWidth={1.5} className="text-neutral-600 shrink-0" />
                <span className="text-sm text-neutral-700">{line}</span>
              </div>
            ))}
          </div>
        </div>

        {wizardOpen && (
          <div className="ed-card p-6 space-y-4" data-testid="affiliate-wizard">
            <p className="ed-eyebrow">{t("affWizardEyebrow")}</p>
            <h3 className="font-serif text-xl">{t("affWizardTitle")}</h3>
            <p className="text-sm text-neutral-600 leading-relaxed">{t("affWizardIntro")}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
              <label className="block">
                <span className="ed-label">{t("affFieldName")}</span>
                <input
                  data-testid="affiliate-input-name"
                  value={form.full_name}
                  onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                  className="w-full mt-1 border border-neutral-300 px-3 py-2 font-serif text-base focus:border-neutral-900 outline-none"
                />
              </label>
              <label className="block">
                <span className="ed-label">{t("affFieldIban")}</span>
                <div className="flex items-stretch gap-0 mt-1">
                  <input
                    data-testid="affiliate-input-iban"
                    value={form.iban}
                    onChange={(e) => updateIban(e.target.value)}
                    placeholder="NL91 ABNA 0417 1643 00"
                    className="flex-1 min-w-0 border border-neutral-300 border-r-0 px-3 py-2 font-mono text-sm uppercase focus:border-neutral-900 outline-none"
                  />
                  <button
                    type="button"
                    onClick={pasteIban}
                    data-testid="affiliate-paste-iban"
                    aria-label={t("affPasteIban")}
                    title={t("affPasteIban")}
                    className="shrink-0 px-3 border border-neutral-300 bg-neutral-50 hover:bg-neutral-900 hover:text-white transition-colors inline-flex items-center justify-center"
                  >
                    <ClipboardPaste size={14} strokeWidth={1.75} />
                  </button>
                </div>
                {/* Live IBAN validation badge — appears once at least a
                    country code + a few chars have been typed. Green ✓ with
                    bank name on success, amber ⚠ with reason on failure. */}
                {form.iban && form.iban.replace(/\s/g, "").length >= 4 && (() => {
                  const v = validateIban(form.iban);
                  if (v.valid) {
                    return (
                      <span
                        data-testid="affiliate-iban-valid"
                        className="mt-1.5 inline-flex items-center gap-1.5 font-mono text-[0.65rem] text-emerald-800"
                      >
                        <Check size={11} strokeWidth={2.5} className="text-emerald-600" />
                        {v.bank
                          ? t("affIbanValidWithBank", { country: v.country, bank: v.bank })
                          : t("affIbanValid", { country: v.country })}
                      </span>
                    );
                  }
                  // Only show the "invalid" hint once the user has typed a
                  // plausible length (avoids nagging while they're still
                  // typing the first characters).
                  const rawLen = form.iban.replace(/\s/g, "").length;
                  if (rawLen < 8) return null;
                  let reason = t("affIbanReasonGeneric");
                  if (v.reason === "unknown_country") reason = t("affIbanReasonCountry", { country: v.country || "??" });
                  else if (v.reason === "wrong_length") reason = t("affIbanReasonLength", { expected: v.expectedLen, actual: v.actualLen });
                  else if (v.reason === "bad_format") reason = t("affIbanReasonFormat");
                  else if (v.reason === "checksum") reason = t("affIbanReasonChecksum");
                  return (
                    <span
                      data-testid="affiliate-iban-invalid"
                      className="mt-1.5 inline-flex items-center gap-1.5 font-mono text-[0.65rem] text-amber-800"
                    >
                      <AlertCircle size={11} strokeWidth={2} className="text-amber-600" />
                      {reason}
                    </span>
                  );
                })()}
              </label>
              <label className="block md:col-span-2">
                <span className="ed-label inline-flex items-center gap-2">
                  {t("affFieldBic")}
                  {form._bic_autofilled && (
                    <span
                      data-testid="affiliate-bic-autofilled"
                      className="font-mono text-[0.55rem] uppercase tracking-[0.15em] bg-emerald-50 border border-emerald-200 text-emerald-800 px-1.5 py-0.5 normal-case"
                    >
                      <Check size={9} strokeWidth={2.5} className="inline mr-1" />
                      {t("affBicAutofilled")}
                    </span>
                  )}
                </span>
                <input
                  data-testid="affiliate-input-bic"
                  value={form.bic}
                  onChange={(e) => setForm((f) => ({ ...f, bic: e.target.value, _bic_autofilled: false }))}
                  placeholder="ABNANL2A"
                  className="w-full mt-1 border border-neutral-300 px-3 py-2 font-mono text-sm uppercase focus:border-neutral-900 outline-none"
                />
              </label>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button
                data-testid="affiliate-wizard-cancel"
                onClick={() => setWizardOpen(false)}
                disabled={busy}
                className="ed-btn ed-btn-outline"
              >
                {t("common_back")}
              </button>
              <button
                data-testid="affiliate-wizard-submit"
                onClick={join}
                disabled={busy}
                className="ed-btn"
              >
                {busy ? "…" : t("affWizardSubmit")}
              </button>
            </div>
          </div>
        )}

        {/* Plain-share dialog: same UX as the sidebar "Aanbevelen" button —
            no affiliate opt-in needed, just share the JobGrid link with
            friends. If the recipient signs up + upgrades, the sender earns
            +14 trial days. */}
        <RecommendDialog
          open={recDialogOpen}
          onClose={() => setRecDialogOpen(false)}
          isAffiliate={false}
          onOptInAffiliate={() => {
            // Bounce the user into the affiliate wizard right below.
            setRecDialogOpen(false);
            setWizardOpen(true);
            setTimeout(() => {
              const el = document.querySelector('[data-testid="affiliate-wizard"]');
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 60);
          }}
        />
      </div>
    );
  }

  // ---- OPTED-IN dashboard state ----
  const accrued = (state.accrued_cents || 0) / 100;
  const threshold = (state.payout_threshold_cents || 2500) / 100;
  const pct = Math.min(100, (accrued / threshold) * 100);

  return (
    <div data-testid="affiliate-panel-dashboard" className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="ed-eyebrow">{t("affDashEyebrow")}</p>
          <h2 className="font-serif text-2xl mt-1 leading-tight">{t("affDashTitle")}</h2>
        </div>
        <button
          type="button"
          data-testid="affiliate-start-partner-tour"
          onClick={() => document.dispatchEvent(new CustomEvent("open-partner-tour"))}
          className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-300 hover:border-neutral-900 px-3 py-2 inline-flex items-center gap-1.5"
        >
          <Sparkles size={12} strokeWidth={1.75} /> {t("affPartnerTourCta")}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { icon: Users, label: t("affStatClicks7d"), value: state.clicks_last_7d },
          { icon: TrendingUp, label: t("affStatConversions"), value: state.conversions_total },
          { icon: Wallet, label: t("affStatAccrued"), value: `€${(state.accrued_cents/100).toFixed(2).replace(".", ",")}` },
          { icon: Check, label: t("affStatPaid"), value: `€${(state.paid_cents/100).toFixed(2).replace(".", ",")}` },
        ].map(({ icon: Icon, label, value }, i) => (
          <div key={i} className="ed-card p-4" data-testid={`affiliate-stat-${i}`}>
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 flex items-center gap-1.5">
              <Icon size={11} strokeWidth={1.75} /> {label}
            </p>
            <p className="font-serif text-2xl mt-2">{value}</p>
          </div>
        ))}
      </div>

      {/* Per-poster-variant click attribution (utm_medium=poster-*) so
          affiliates see which physical print is pulling its weight. Hidden
          entirely while no click has ever been UTM-tagged so the panel
          stays clean for brand-new affiliates. */}
      {state.clicks_by_variant && Object.values(state.clicks_by_variant).some((n) => n > 0) && (
        <div className="ed-card p-6" data-testid="affiliate-poster-attribution">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div>
              <p className="ed-eyebrow">{t("affPosterAttribution")}</p>
              <p className="text-xs text-neutral-500 mt-1">{t("affPosterAttributionHint")}</p>
            </div>
            {state.winning_variant && (
              <p
                data-testid="affiliate-winning-variant"
                className="font-mono text-[0.6rem] uppercase tracking-[0.15em] bg-emerald-700 text-[#F9F8F6] px-2 py-1"
                title={t("affPosterWinnerHint")}
              >
                {t("affPosterWinnerLabel").replace("{v}", t(`affPoster${state.winning_variant.charAt(0).toUpperCase()}${state.winning_variant.slice(1)}`))}
              </p>
            )}
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3">
            {[
              ["classic", t("affPosterClassic")],
              ["banner", t("affPosterBanner")],
              ["quote", t("affPosterQuote")],
            ].map(([key, label]) => {
              const clicks = state.clicks_by_variant?.[key] || 0;
              const conv = state.conversions_by_variant?.[key] || 0;
              const commission = state.commission_by_variant?.[key] || 0;
              const rate = clicks > 0 ? Math.round((conv / clicks) * 1000) / 10 : 0;
              const isWinner = state.winning_variant === key;
              return (
                <div
                  key={key}
                  data-testid={`affiliate-variant-${key}`}
                  className={`border p-3 ${isWinner ? "border-emerald-700 bg-emerald-50/40" : "border-neutral-200"}`}
                >
                  <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">{label}</p>
                  <p className="font-serif text-2xl mt-1">{clicks}</p>
                  <p className="font-mono text-[0.55rem] text-neutral-500 uppercase tracking-[0.1em]">{t("affPosterClicksUnit")}</p>
                  <div className="mt-2 pt-2 border-t border-neutral-200 space-y-0.5">
                    <p
                      className="font-mono text-[0.65rem] text-neutral-700"
                      data-testid={`affiliate-variant-${key}-conv`}
                    >
                      {conv} {t("affPosterConversionsUnit")} · €{(commission / 100).toFixed(2).replace(".", ",")}
                    </p>
                    <p
                      className="font-mono text-[0.55rem] text-neutral-500 uppercase tracking-[0.1em]"
                      data-testid={`affiliate-variant-${key}-rate`}
                    >
                      {rate}% {t("affPosterCvrUnit")}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Payout progress bar */}
      <div className="ed-card p-6">
        <div className="flex items-baseline justify-between mb-2">
          <p className="ed-eyebrow">{t("affPayoutProgress")}</p>
          <p className="font-mono text-xs text-neutral-600">
            €{accrued.toFixed(2).replace(".", ",")} / €{threshold.toFixed(0)}
          </p>
        </div>
        <div className="h-2 bg-neutral-100 border border-neutral-200 overflow-hidden">
          <div className="h-full bg-emerald-700 transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        {state.can_request_payout ? (
          <button
            data-testid="affiliate-payout-request"
            onClick={requestPayout}
            disabled={busy}
            className="ed-btn mt-4"
          >
            {busy ? "…" : t("affPayoutCta")}
          </button>
        ) : (
          <p className="font-mono text-[0.65rem] text-neutral-500 mt-3">
            {t("affPayoutHint", { need: (threshold - accrued).toFixed(2).replace(".", ",") })}
          </p>
        )}
      </div>

      {/* Share tools */}
      <div className="ed-card p-6">
        <p className="ed-eyebrow">{t("affShareEyebrow")}</p>
        <p className="text-sm text-neutral-600 mt-1 leading-relaxed">{t("affShareIntro")}</p>
        <div className="mt-4 p-4 bg-neutral-50 border border-neutral-200 font-mono text-xs break-all text-neutral-700" data-testid="affiliate-ref-url">
          {window.location.origin}/api/r/{state.ref_code}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button data-testid="affiliate-open-share-modal" onClick={() => setShareOpen(true)} className="ed-btn inline-flex items-center gap-2 bg-neutral-900 text-[#F9F8F6] hover:bg-neutral-700">
            <Share2 size={12} strokeWidth={1.75} /> {t("affShareNow")}
          </button>
          <button data-testid="affiliate-copy-banner" onClick={copyBanner} className="ed-btn ed-btn-outline inline-flex items-center gap-2">
            <Copy size={12} strokeWidth={1.75} /> {t("affCopyBanner")}
          </button>
          <button data-testid="affiliate-copy-link" onClick={copyPlainLink} className="ed-btn ed-btn-outline inline-flex items-center gap-2">
            <ExternalLink size={12} strokeWidth={1.75} /> {t("affCopyLink")}
          </button>
        </div>
      </div>

      {/* Live banner preview + optional logo/name endorsement */}
      <div className="ed-card p-6">
        <p className="ed-eyebrow">{t("affBannerPreviewEyebrow")}</p>
        <h3 className="font-serif text-xl mt-2 leading-tight">{t("affBannerPreviewTitle")}</h3>
        <div className="mt-5">
          <BannerPreview
            logoDataUrl={state?.logo_data_url || ""}
            displayName={state?.display_name || ""}
          />
        </div>
        <LogoEndorsementUploader
          t={t}
          currentUrl={state?.logo_data_url || ""}
          onChanged={(url) => setState((s) => ({ ...(s || {}), logo_data_url: url }))}
        />
        <DisplayNameEditor
          t={t}
          currentName={state?.display_name || ""}
          onChanged={(name) => setState((s) => ({ ...(s || {}), display_name: name }))}
        />
      </div>

      {/* Print-ready A4 poster templates — its own card so it doesn't
          compete with the share-link buttons and gets the visual weight it
          deserves. Language picker + 3 preview thumbnails + optional
          custom testimonial that overrides the curated default on all
          three variants. */}
      <PosterTemplatesSection refCode={state.ref_code} />

      {/* Payment details recap */}
      <div className="ed-card p-6">
        <p className="ed-eyebrow">{t("affPaymentDetails")}</p>
        <dl className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4 font-mono text-xs">
          <div><dt className="text-neutral-500">{t("affFieldName")}</dt><dd className="mt-1">{state.full_name || "—"}</dd></div>
          <div><dt className="text-neutral-500">IBAN</dt><dd className="mt-1">{state.iban || "—"}</dd></div>
          <div><dt className="text-neutral-500">BIC</dt><dd className="mt-1">{state.bic || "—"}</dd></div>
        </dl>
      </div>

      <StripeConnectCard state={state} onChange={load} t={t} />

      {/* Partner story CTA — only for affiliates who've earned at least one
          conversion, so we don't ask people to write a testimonial before
          they've experienced the payout flow. */}
      {(state.conversions_total || 0) > 0 && (
        <div className="ed-card p-6 bg-gradient-to-br from-[#F9F8F6] to-[#F0EFEA] border-black/10" data-testid="affiliate-story-cta">
          <div className="flex items-start gap-4">
            <Quote size={22} strokeWidth={1.25} className="text-amber-600 shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <p className="ed-eyebrow">{t("affStoryEyebrow")}</p>
              <p className="font-serif text-lg mt-2 leading-tight">{t("affStoryTitle")}</p>
              <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{t("affStoryBody")}</p>
              <button
                data-testid="affiliate-share-story"
                onClick={() => setReviewOpen(true)}
                className="ed-btn mt-4"
              >
                {t("affStoryCta")}
              </button>
            </div>
          </div>
        </div>
      )}
      <ReviewSubmitModal open={reviewOpen} onClose={() => setReviewOpen(false)} category="partner" />
      <AffiliateShareModal open={shareOpen} onClose={() => setShareOpen(false)} refCode={state.ref_code} />
    </div>
  );
}

// Small card that surfaces the Stripe Connect upgrade option. Existing
// affiliates keep working with the IBAN flow — this is a soft migration
// (option 3a in the setup). Once onboarded, admins can pay them via
// Stripe Transfer instead of manual SEPA.
function StripeConnectCard({ state, onChange, t }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/affiliate/stripe/connect/status")
      .then((r) => { if (alive) setStatus(r.data); })
      .catch(() => { if (alive) setStatus({ connected: false, onboarding_complete: false }); });
    return () => { alive = false; };
  }, [state?.is_affiliate]);

  // If already fully onboarded, show a compact confirmation instead of a
  // big upgrade prompt.
  if (status?.onboarding_complete) {
    return (
      <div className="ed-card p-5 border-emerald-200 bg-emerald-50/50" data-testid="stripe-connect-ready">
        <div className="flex items-start gap-3">
          <BadgeCheck size={18} strokeWidth={1.5} className="text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-serif text-base leading-tight">{t("affStripeReadyTitle")}</p>
            <p className="text-xs text-neutral-600 mt-1 leading-relaxed">{t("affStripeReadyBody")}</p>
          </div>
        </div>
      </div>
    );
  }

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/affiliate/stripe/connect/start", {});
      if (r.data?.onboarding_url) {
        window.location.href = r.data.onboarding_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("affStripeStartFailed"));
      setBusy(false);
    }
  };

  const midOnboarding = status?.connected && !status?.onboarding_complete;

  return (
    <div
      data-testid="stripe-connect-upgrade-card"
      className="relative overflow-hidden bg-gradient-to-br from-[#635BFF]/95 to-[#4F47D6] text-white p-6 sm:p-8 shadow-xl"
    >
      <div className="absolute -top-4 -right-4 opacity-15 pointer-events-none">
        <Wallet size={140} strokeWidth={1.1} />
      </div>
      <p className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-white/80">
        {t("affStripeEyebrow")}
      </p>
      <h3 className="font-serif text-2xl sm:text-3xl mt-3 leading-tight max-w-lg">
        {midOnboarding ? t("affStripeResumeTitle") : t("affStripeUpgradeTitle")}
      </h3>
      <p className="text-sm text-white/90 mt-3 max-w-lg leading-relaxed">
        {midOnboarding ? t("affStripeResumeBody") : t("affStripeUpgradeBody")}
      </p>
      <button
        onClick={start}
        disabled={busy}
        data-testid="stripe-connect-start"
        className="mt-6 px-6 py-3 bg-white text-[#635BFF] font-mono text-xs uppercase tracking-[0.15em] hover:bg-neutral-100 transition-colors disabled:opacity-60 inline-flex items-center gap-2 shadow-md"
      >
        {busy ? "…" : (midOnboarding ? t("affStripeResumeCta") : t("affStripeUpgradeCta"))}
      </button>
    </div>
  );
}


/**
 * Small controls block that lets an opted-in affiliate upload their own
 * brand logo. When set, the logo is rendered inside the email banner as
 * "Aanbevolen door <logo>" — perfect for career coaches, HR agencies or
 * bloggers who want to associate their name with the recommendation.
 *
 * We downscale on the client (max 400 px wide, converted to PNG) so the
 * stored data-URL stays well under the 1 MB cap enforced by the backend.
 */
function LogoEndorsementUploader({ t, currentUrl, onChanged }) {
  const [busy, setBusy] = useState(false);

  const downscale = (file) => new Promise((resolve, reject) => {
    // SVGs are already vector — upload as-is (converted to data URL).
    if (file.type === "image/svg+xml") {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const MAX_W = 400;
        const scale = Math.min(1, MAX_W / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        // Preserve transparency by defaulting to PNG.
        resolve(canvas.toDataURL("image/png", 0.92));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (file.size > 2_000_000) {
      toast.error(t("affLogoTooLarge"));
      return;
    }
    setBusy(true);
    try {
      const data_url = await downscale(file);
      const r = await api.post("/affiliate/logo", { data_url });
      onChanged?.(r.data?.logo_data_url || data_url);
      toast.success(t("affLogoSaved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("affLogoSaveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/affiliate/logo");
      onChanged?.("");
      toast.success(t("affLogoRemoved"));
    } catch {
      toast.error(t("affLogoSaveFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 pt-6 border-t border-neutral-200" data-testid="affiliate-logo-uploader">
      <p className="ed-eyebrow flex items-center gap-2">
        <ImageIcon size={12} strokeWidth={1.5} /> {t("affLogoEyebrow")}
      </p>
      <p className="text-sm text-neutral-600 mt-2 leading-relaxed max-w-2xl">
        {t("affLogoBody")}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-4">
        {currentUrl ? (
          <div className="flex items-center gap-3 border border-neutral-200 bg-white px-4 py-3">
            <img
              src={currentUrl}
              alt="Aanbevolen door"
              data-testid="affiliate-logo-current"
              style={{ maxHeight: "40px", maxWidth: "160px", display: "block" }}
            />
            <button
              type="button"
              data-testid="affiliate-logo-remove"
              onClick={remove}
              disabled={busy}
              className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-300 hover:border-red-600 hover:text-red-600 transition-colors px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <Trash2 size={11} strokeWidth={1.75} /> {t("affLogoRemoveCta")}
            </button>
          </div>
        ) : (
          <div className="text-xs text-neutral-500 font-mono uppercase tracking-[0.12em]">
            {t("affLogoNone")}
          </div>
        )}
        <label
          className="ed-btn ed-btn-outline inline-flex items-center gap-2 cursor-pointer"
          data-testid="affiliate-logo-upload-label"
        >
          <Upload size={12} strokeWidth={1.75} />
          {busy ? t("affLogoUploading") : (currentUrl ? t("affLogoReplaceCta") : t("affLogoUploadCta"))}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            onChange={onFile}
            disabled={busy}
            data-testid="affiliate-logo-file"
            className="hidden"
          />
        </label>
      </div>
      <p className="mt-3 text-xs text-neutral-500 leading-relaxed">
        {t("affLogoHint")}
      </p>
    </div>
  );
}

/**
 * DisplayNameEditor — optional short brand-name that appears next to the
 * affiliate's logo on the banner as "Aanbevolen door <name>". Independent
 * from the logo: use either, both, or neither.
 */
function DisplayNameEditor({ t, currentName, onChanged }) {
  const [value, setValue] = React.useState(currentName || "");
  const [busy, setBusy] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    setValue(currentName || "");
    setDirty(false);
  }, [currentName]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/affiliate/display-name", { display_name: value.trim() });
      onChanged?.(r.data?.display_name || value.trim());
      toast.success(t("affDisplayNameSaved"));
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("affDisplayNameError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5 pt-5 border-t border-neutral-200">
      <p className="ed-eyebrow">{t("affDisplayNameEyebrow")}</p>
      <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
        {t("affDisplayNameBody")}
      </p>
      <div className="mt-3 flex flex-wrap items-stretch gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => { setValue(e.target.value); setDirty(true); }}
          placeholder={t("affDisplayNamePlaceholder")}
          maxLength={60}
          data-testid="affiliate-display-name-input"
          className="flex-1 min-w-[200px] border border-neutral-300 px-3 py-2 text-sm focus:border-neutral-900 outline-none"
        />
        <button
          type="button"
          data-testid="affiliate-display-name-save"
          onClick={save}
          disabled={busy || !dirty}
          className="ed-btn inline-flex items-center gap-2 disabled:opacity-50"
        >
          {busy ? "…" : t("affDisplayNameSave")}
        </button>
      </div>
      <p className="mt-2 text-xs text-neutral-500">
        {t("affDisplayNameHint")}
      </p>
    </div>
  );
}

